import { Component, type ErrorInfo, type ReactNode } from "react";
import { logger } from "./logger";

interface ErrorBoundaryProps {
  /** Content to display when an error occurs. */
  fallback?: ReactNode;
  /** Child components to render. */
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * React error boundary that catches rendering errors in its subtree.
 *
 * Logs the error via the logger service and renders a fallback UI.
 * Wrap this around sections of the app that should degrade gracefully
 * rather than crash the entire page.
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={<p>Something went wrong.</p>}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error("Uncaught error in component tree", {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div role="alert" className="flex min-h-[200px] items-center justify-center p-8">
            <div className="text-center">
              <h2 className="text-lg font-semibold">Something went wrong</h2>
              <p className="mt-2 text-sm text-gray-600">Please try refreshing the page.</p>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
